import { useMemo, useState } from 'react';
import { Package } from 'lucide-react';
import type { TopItemsReport, Unit } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import type { BarDatum } from '../model/chart-data.js';
import { ReportCard } from './ReportCard.js';
import { TopItemsBar } from './TopItemsBar.js';

type Tab = 'moved' | 'low';

export interface TopItemsBlockProps {
  query: UseQueryResult<TopItemsReport, ApiClientError>;
}

/**
 * Mahsulotlar bo'yicha TOP — ikkita ro'yxat bitta kartochkada.
 *
 * Ikkalasi ham "qaysi mahsulotga qarash kerak" degan bitta savolga javob
 * beradi, faqat turli tomondan: biri ko'p harakatdagisi, ikkinchisi
 * tugayotgani. Ikki alohida kartochka bo'lsa, ekranda bir xil ko'rinishdagi
 * ikki blok yonma-yon turib, farqi ko'rinmay qolardi.
 */
export const TopItemsBlock = ({ query }: TopItemsBlockProps) => {
  const [tab, setTab] = useState<Tab>('moved');

  const items = useMemo<BarDatum[]>(() => {
    if (query.data === undefined) return [];
    return tab === 'moved'
      ? query.data.moved.map((row) => ({
          id: row.nomenclatureId,
          label: row.name,
          hint: unitText(row.unit as Unit),
          value: row.qty,
        }))
      : query.data.low.map((row) => ({
          id: row.nomenclatureId,
          label: row.name,
          /* Chegara MUHIM: "40 dona" o'zi kam ekanini aytmaydi —
             faqat 100 ga nisbatan kam ekani ma'no beradi. */
          hint: t('report.topMin', { min: row.minStock, unit: unitText(row.unit as Unit) }),
          value: row.qty,
          severity: row.minStock > 0 ? row.qty / row.minStock : undefined,
        }));
  }, [query.data, tab]);

  return (
    <ReportCard
      icon={Package}
      tone={tab === 'low' ? 'warn' : 'success'}
      title={t('report.topTitle')}
      hint={tab === 'moved' ? t('report.topMovedHint') : t('report.topLowHint')}
      pending={query.isPending}
      error={query.error ?? null}
      empty={items.length === 0}
      emptyText={tab === 'moved' ? t('report.topMovedEmpty') : t('report.topLowEmpty')}
      actions={
        <Segmented
          label={t('report.topTitle')}
          value={tab}
          options={[
            { value: 'moved', label: t('report.topMoved') },
            { value: 'low', label: t('report.topLow') },
          ]}
          onChange={setTab}
        />
      }
    >
      <TopItemsBar items={items} />
    </ReportCard>
  );
};
